const THRESHOLD = 12; // px — how close an edge must be to snap

/**
 * Given a dragging window's raw position and size, compute the nearest
 * snapped position by aligning any of its four edges to any edge of the
 * windows in `otherLayouts`. X and Y axes are snapped independently.
 *
 * @param {number} dragX
 * @param {number} dragY
 * @param {number} dragW
 * @param {number} dragH
 * @param {Array<{x,y,width,height}>} otherLayouts
 * @returns {{ x: number, y: number }}
 */
export function computeSnap(dragX, dragY, dragW, dragH, otherLayouts) {
  let snapX = dragX;
  let snapY = dragY;
  let bestX = THRESHOLD;
  let bestY = THRESHOLD;

  const dragR = dragX + dragW;
  const dragB = dragY + dragH;

  for (const other of otherLayouts) {
    const { x: ox, y: oy, width: ow, height: oh } = other;
    const otherR = ox + ow;
    const otherB = oy + oh;

    // X: compare each of our two vertical edges against each of their two
    for (const [ourEdge, theirEdge, offset] of [
      [dragX, ox,     0],
      [dragX, otherR, 0],
      [dragR, ox,    -dragW],
      [dragR, otherR,-dragW],
    ]) {
      const d = Math.abs(ourEdge - theirEdge);
      if (d < bestX) { bestX = d; snapX = theirEdge + offset; }
    }

    // Y: compare each of our two horizontal edges against each of their two
    for (const [ourEdge, theirEdge, offset] of [
      [dragY, oy,     0],
      [dragY, otherB, 0],
      [dragB, oy,    -dragH],
      [dragB, otherB,-dragH],
    ]) {
      const d = Math.abs(ourEdge - theirEdge);
      if (d < bestY) { bestY = d; snapY = theirEdge + offset; }
    }
  }

  return { x: snapX, y: snapY };
}

/**
 * After a resize completes, snap the moved edge(s) to nearby window edges.
 * `direction` is react-rnd's handle string, e.g. 'right', 'bottomLeft', 'top'.
 * Left/top handle snaps shift the window's origin; right/bottom snaps only
 * change the size. Position and size are returned as a combined object.
 *
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {string} direction
 * @param {Array<{x,y,width,height}>} otherLayouts
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function computeResizeSnap(x, y, width, height, direction, otherLayouts) {
  let nx = x, ny = y, nw = width, nh = height;
  let bestX = THRESHOLD, bestY = THRESHOLD;

  const rightEdge  = x + width;
  const bottomEdge = y + height;

  const snapRight  = /right/i.test(direction);
  const snapLeft   = /left/i.test(direction);
  const snapBottom = /bottom/i.test(direction);
  const snapTop    = /top/i.test(direction);

  for (const other of otherLayouts) {
    const ox = other.x, oy = other.y;
    const otherR = ox + other.width;
    const otherB = oy + other.height;

    // X axis — only the moving edge snaps
    if (snapRight) {
      for (const target of [ox, otherR]) {
        const d = Math.abs(rightEdge - target);
        if (d < bestX) { bestX = d; nw = target - nx; }
      }
    } else if (snapLeft) {
      for (const target of [ox, otherR]) {
        const d = Math.abs(x - target);
        if (d < bestX) { bestX = d; nx = target; nw = rightEdge - target; }
      }
    }

    // Y axis — only the moving edge snaps
    if (snapBottom) {
      for (const target of [oy, otherB]) {
        const d = Math.abs(bottomEdge - target);
        if (d < bestY) { bestY = d; nh = target - ny; }
      }
    } else if (snapTop) {
      for (const target of [oy, otherB]) {
        const d = Math.abs(y - target);
        if (d < bestY) { bestY = d; ny = target; nh = bottomEdge - target; }
      }
    }
  }

  return { x: nx, y: ny, width: nw, height: nh };
}
